<template>
    <x-form ref="selectInsurersForm" @submit.prevent="submit">
        <x-container fluid>
            <template v-if="loading">
                <x-layout>
                    <x-flex xs12>
                        <loading-spinner class="compact"/>
                    </x-flex>
                </x-layout>
            </template>

            <template v-else-if="!submissionComplete">
                <x-layout class="question-set">
                    <x-flex xs2 offset-xs1>
                        <h3 class="question-set-title">Select insurers</h3>
                    </x-flex>

                    <x-flex xs6 class="question-set-questions">
                        <x-container>
                            <x-layout>
                                <x-flex xs12>
                                    <add-insurers
                                        :selectedInsurersUsers.sync="selectedInsurersUsers"
                                        :insurers="insurers"
                                        :defaultInsurerIds="commitmentInsurerIds"
                                        :mandatoryInsurers="insurersSelectedInAdditionalInformation"
                                        :captiveInsurer="captiveInsurer"
                                    />
                                </x-flex>
                            </x-layout>
                        </x-container>
                    </x-flex>
                </x-layout>

                <x-layout class="action-set" align-center>
                    <x-flex xs6 offset-xs3 class="action-set-actions">
                        <x-container>
                            <x-layout>
                                <x-flex xs6>
                                    <x-btn
                                        class="mr-4"
                                        color="tertiary"
                                        @click="$emit('submission:navigate-to', currentStep - 1)"
                                    >
                                        <i class="material-icons mr-2">arrow_back</i>

                                        {{ submission.id > 0 ? 'Back' : 'Cancel' }}
                                    </x-btn>

                                    <delete-quote-button v-if="submission.status === 'draft'" :submissionId="submission.id" />
                                </x-flex>

                                <x-flex xs6 text-xs-right>
                                    <x-btn
                                        v-if="isManualFirmOrder"
                                        class="wide"
                                        type="submit"
                                        color="primary"
                                        :disabled="Object.keys(this.selectedInsurersUsers).length < 1"
                                        @click="createManualFirmOrder"
                                    >
                                        Next

                                        <i class="material-icons ml-2">trending_flat</i>
                                    </x-btn>

                                    <x-btn
                                        v-else
                                        class="wide"
                                        type="submit"
                                        color="primary"
                                        :disabled="loading"
                                    >
                                        Submit quote

                                        <i class="material-icons ml-2">trending_flat</i>
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </x-container>
                    </x-flex>
                </x-layout>
            </template>

            <template v-else>
                <x-layout>
                    <x-flex class="submission-complete-message" xs12 text-xs-center>
                        <div>
                            <circle-icon size="50" color="#7CE0D3" icon-size="36" icon-color="#173F35">check</circle-icon>
                        </div>

                        <div>
                            <h2 class="my-4">Submission is in the market</h2>

                            <p>Your submission has been successfully submitted to the marketplace.</p>
                        </div>
                    </x-flex>
                </x-layout>

                <x-layout>
                    <x-flex class="submission-complete-message" xs12 mt-5 text-xs-center>
                        <x-btn
                            data-test="back_to_dashboard"
                            color="primary"
                            @click="returnToDashboard"
                        >
                            Back to Dashboard
                        </x-btn>
                    </x-flex>
                </x-layout>
            </template>
        </x-container>

        <modal
            ref="sofConfirmation"
            title="Statement of Facts"
            actionText="Confirm"
            confirmation
            textLeft
        >
            <statement-of-fact />
        </modal>

    </x-form>
</template>

<script>
    import DeleteQuoteButton from '~/components/Broker/UIComponents/DeleteQuoteButton';
    import CircleIcon from '~/components/Shared/UIComponents/CircleIcon';
    import AddInsurers from "../UIComponents/AddInsurers";
    import StatementOfFact from "~/components/Shared/UIComponents/StatementOfFact";
    import Modal from '~/components/Shared/UIComponents/Modal';
    import rules from '~/mixins/rules';

    export default {
        name: 'SelectInsurers',
        components: {
            DeleteQuoteButton,
            CircleIcon,
            AddInsurers,
            StatementOfFact,
            Modal
        },
        mixins: [rules],
        props: ['submission', 'currentStep', 'selectedInsurersUsersList', 'isManualFirmOrder'],
        data() {
            return {
                loading: true,
                submissionComplete: false,
                insurers: [],
                selectedInsurersUsers: {}
            }
        },
        computed: {
            commitmentInsurerIds() {
                if (this.submission.commitment_declaration) {
                    return this.submission.commitment_declaration.lines.map(line => line.insurer_company_id);
                }
            },
            insurersSelectedInAdditionalInformation() {
                let insurerCompanyIds = [];
                this.submission.information_sections.forEach(function (value) {
                    insurerCompanyIds = insurerCompanyIds.concat(value.insurer_company_ids);
                });
                return [...new Set(insurerCompanyIds)];
            },
            isCaptiveReinsurance() {
                return this.submission.transaction_type === 'captive_reinsurance';
            },
            insurerType() {
                if (this.submission.hasOwnProperty('captive_insurer_company_id') && this.submission.captive_insurer_company_id) {
                    return 'reinsurer';
                }

                return 'insurer'
            },
            isMarketEnquiry() {
                return this.submission.is_manual_firm_order === 0;
            },
            captiveInsurer() {
                return this.isManualFirmOrder && this.isCaptiveReinsurance ? this.submission.captive_insurer_company : {};
            }
        },
        async mounted() {
            await this.getInsurers();

            if (Object.keys(this.selectedInsurersUsers).length === 0 && Object.keys(this.selectedInsurersUsersList).length > 0) {
                this.selectedInsurersUsers = this.selectedInsurersUsersList;
            }

            this.loading = false;
        },
        methods: {
            async getInsurers() {
                if (this.submission.facility_id !== null) {
                    this.insurers = (await axios.get(this.$root.api(`insurer/facility/${this.submission.facility_id}`), {responseType: 'json'})).data.data;
                } else {
                    this.insurers = (await axios.get(this.$root.api('insurer'), {responseType: 'json'})).data.data;
                }
            },
            async submit() {
                if (this.$refs.selectInsurersForm.validate()) {

                    if (!this.submission.facility?.do_not_display_sof && (this.isMarketEnquiry && await this.$refs.sofConfirmation.open() === false)) {
                        return;
                    }

                    this.loading = true;
                    axios.post(
                        this.$root.api(`broker/submission/${this.submission.id}/offer-to-market`), {
                            insurersUsers: this.selectedInsurersUsers,
                            confirmed_sof: true
                        }
                    ).then(() => {
                        this.submissionComplete = true;
                        this.$emit('submission:complete');
                        this.loading = false;
                    });
                }
            },
            createManualFirmOrder() {
                this.$emit('submission:navigate-to', this.currentStep + 1);
            },
            returnToDashboard() {
                this.$router.push('/agent/dashboard');
            }
        },
        watch: {
            selectedInsurersUsers() {
                this.$emit('update:selectedInsurersUsersList', this.selectedInsurersUsers);
            },
            insurers() {
                this.$emit('update:insurers', this.insurers);
            }
        }
    }
</script>

<style lang="scss" scoped>
    div.container.step-form {
        margin-top: 66px;
    }

    .x-input :deep(input::placeholder) {
        text-transform: capitalize;
    }

    .x-select-list :deep() {
        .x-list {
            .x-list__tile {
                margin: 2px;
                padding: 15px;
                border-radius: $border-radius-small;
            }

            .buttons-top-tile {
                .x-list__tile {
                    justify-content: flex-end;
                    margin: -7px -7px 0;
                    padding-top: 0;
                    padding-bottom: 0;

                    button {
                        flex-grow: 0;
                        width: 120px;
                    }
                }
            }
        }
    }

    table.insurer-list {
        width: 100%;
        font-size: rem(14px);
        table-layout: fixed;

        tbody {
            tr:nth-child(odd) {
                background: $color-black;
            }

            td {
                padding: 6px;
                vertical-align: middle;
            }

            .x-btn {
                height: 20px;
                font-size: rem(12px);
                opacity: 0;
                transition: opacity $transition-length;
            }

            tr:hover .x-btn {
                opacity: 1;
            }
        }
    }

    .submission-complete-message {
        h2 {
            font-size: rem(40px);
            line-height: 1em;
        }

        p {
            font-family: $font-title;
            font-size: rem(24px);
        }

        button.x-btn {
            height: 56px;
        }
    }
</style>
